import { useTranslation } from 'react-i18next';

import { NfcCardOrderStatus, NfcCardSort, NfcCardState, isNfcCardTransactionType } from 'lib/models/nfcCards';
import { NfcReadFailure } from 'lib/webNfc';

/**
 * The Spanish copy for the card states, the status of the orders loaded on a card and the
 * kinds of balance movement, what the listing sorts by and why a card could not be read, switched to literal keys so they stay greppable and typed against the resource file.
 */
function useNfcCardLabels() {
	const { t } = useTranslation();

	function stateLabel(state: NfcCardState): string {
		switch (state) {
			case 'active':
				return t('nfc:stateActive');
			case 'blocked':
				return t('nfc:stateBlocked');
			case 'expired':
				return t('nfc:stateExpired');
		}
	}

	function orderStatusLabel(status: NfcCardOrderStatus): string {
		switch (status) {
			case 'active':
				return t('nfc:orderActive');
			case 'closed':
				return t('nfc:orderClosed');
			case 'cancelled':
				return t('nfc:orderCancelledStatus');
		}
	}

	/** A movement the panel does not name is shown as the API wrote it. */
	function transactionTypeLabel(status: string): string {
		if (!isNfcCardTransactionType(status)) return status;

		switch (status) {
			case 'approved':
				return t('nfc:transactionLoad');
			case 'reset':
				return t('nfc:transactionReset');
			case 'order':
				return t('nfc:transactionOrder');
			case 'order_refund_approved':
				return t('nfc:transactionRefund');
		}
	}

	/** A sort of the listing, worded for its field: what goes first. */
	function sortLabel({ field, direction }: NfcCardSort): string {
		const asc = direction === 'asc';

		switch (field) {
			case 'balance':
				return asc ? t('nfc:sortBalanceAsc') : t('nfc:sortBalanceDesc');
			case 'operations':
				return asc ? t('nfc:sortOperationsAsc') : t('nfc:sortOperationsDesc');
			case 'expiresAt':
				return asc ? t('nfc:sortExpiresAtAsc') : t('nfc:sortExpiresAtDesc');
			case 'responsable':
				return asc ? t('nfc:sortResponsableAsc') : t('nfc:sortResponsableDesc');
		}
	}

	function readFailureLabel(failure: NfcReadFailure): string {
		switch (failure) {
			case 'denied':
				return t('nfc:scanDenied');
			case 'unsupported':
				return t('nfc:scanUnsupported');
			case 'startFailed':
				return t('nfc:scanStartFailed');
			case 'readFailed':
				return t('nfc:scanReadFailed');
			case 'invalidUid':
				return t('nfc:scanInvalidUid');
		}
	}

	/**
	 * How a sentence names a card: by its label, since the panel never shows its UID
	 * (MUTE-606).
	 */
	function cardName(label?: string | null): string {
		return label ? t('nfc:cardNamed', { label }) : t('nfc:cardUnnamed');
	}

	return { stateLabel, orderStatusLabel, transactionTypeLabel, sortLabel, readFailureLabel, cardName };
}

export default useNfcCardLabels;
